import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabaseClient";

const LoanModal = ({ artwork, onClose, onSaved }) => {
  const { t } = useTranslation();
  const isOnLoan = artwork.loan_status === "on_loan" || artwork.availability === "on_loan";

  const [form, setForm] = useState({
    institution: artwork.storage_location_detail || "",
    contact: "",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    contract_ref: "",
    insurance_required: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isOnLoan) {
        // Retour de prêt
        const { error: err } = await supabase
          .from("artworks")
          .update({
            loan_status: "none",
            availability: "available",
            storage_location_detail: null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", artwork.id);
        if (err) throw err;
      } else {
        // Nouveau prêt
        const { error: err } = await supabase
          .from("artworks")
          .update({
            loan_status: "on_loan",
            availability: "on_loan",
            storage_location_detail: form.institution,
            updated_at: new Date().toISOString(),
          })
          .eq("id", artwork.id);
        if (err) throw err;
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.message || t("artworkModals.loan.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md p-8 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-serif text-xl">
            {isOnLoan ? t("artworkModals.loan.returnTitle") : t("artworkModals.loan.title")}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-black">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-500 font-serif italic mb-6">
          {artwork.title}{artwork.artist ? ` — ${artwork.artist}` : ""}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">
            {error}
          </div>
        )}

        {isOnLoan ? (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            {t("artworkModals.loan.currentInfo", { institution: artwork.storage_location_detail || t("artworkModals.loan.unknownInstitution") })}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("artworkModals.loan.fields.institution")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.institution}
                  onChange={(e) => setF("institution", e.target.value)}
                  required
                  placeholder={t("artworkModals.loan.placeholders.institution")}
                  className="w-full border border-gray-300 p-2 rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("artworkModals.loan.fields.contact")}</label>
                <input
                  type="text"
                  value={form.contact}
                  onChange={(e) => setF("contact", e.target.value)}
                  placeholder={t("artworkModals.loan.placeholders.contact")}
                  className="w-full border border-gray-300 p-2 rounded text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("artworkModals.loan.fields.startDate")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setF("start_date", e.target.value)}
                    required
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("artworkModals.loan.fields.endDate")}</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setF("end_date", e.target.value)}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("artworkModals.loan.fields.contractReference")}</label>
                <input
                  type="text"
                  value={form.contract_ref}
                  onChange={(e) => setF("contract_ref", e.target.value)}
                  placeholder={t("artworkModals.loan.placeholders.contractReference")}
                  className="w-full border border-gray-300 p-2 rounded text-sm"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.insurance_required}
                    onChange={(e) => setF("insurance_required", e.target.checked)}
                    className="rounded"
                  />
                  {t("artworkModals.loan.fields.insuranceRequired")}
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("artworkModals.common.notes")}</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setF("notes", e.target.value)}
                  rows={3}
                  placeholder={t("artworkModals.loan.placeholders.notes")}
                  className="w-full border border-gray-300 p-2 rounded text-sm resize-none"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="btn-artsy btn-secondary-artsy" disabled={saving}>
                {t("artworkModals.common.cancel")}
              </button>
              <button type="submit" disabled={saving} className="btn-artsy btn-primary-artsy">
                {saving ? t("artworkModals.common.saving") : t("artworkModals.loan.actions.confirmLoan")}
              </button>
            </div>
          </form>
        )}

        {isOnLoan && (
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="btn-artsy btn-secondary-artsy" disabled={saving}>
              {t("artworkModals.common.cancel")}
            </button>
            <button onClick={handleSubmit} disabled={saving} className="btn-artsy btn-primary-artsy" style={{ background: "#16a34a" }}>
              {saving ? t("artworkModals.common.saving") : t("artworkModals.loan.actions.confirmReturn")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanModal;
